/**
 * Garantii (warranty) module.
 *
 * - Fetches existing warranty requests for the current apartment.
 * - Handles new-request form (text + file uploads).
 * - Tab filtering (all / avatud / toos / lahendatud).
 * - Detail modal with status timeline + attachments.
 *
 * Init is a no-op unless [data-warranty-root] is present.
 */

const STATUS_LABELS = {
  avatud: "Avatud",
  toos: "Töös",
  lahendatud: "Lahendatud",
};

// Outlined pill look — matches LiviData Filament badges.
// Classes reference the custom `status` palette in tailwind.config.js.
const STATUS_COLORS = {
  avatud:
    "bg-status-avatud-bg text-status-avatud-fg ring-1 ring-inset ring-status-avatud-ring",
  toos: "bg-status-toos-bg text-status-toos-fg ring-1 ring-inset ring-status-toos-ring",
  lahendatud:
    "bg-status-lahendatud-bg text-status-lahendatud-fg ring-1 ring-inset ring-status-lahendatud-ring",
};

const STATUS_FALLBACK = "bg-grey-100 text-dark ring-1 ring-inset ring-grey-200";

const MAX_FILES = 5;
const MAX_STANDARD_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const MAX_VIDEO_FILE_SIZE = 50 * 1024 * 1024; // 50 MB
const MAX_TOTAL_SIZE = 100 * 1024 * 1024; // 100 MB
const VIDEO_MIMES = ["video/mp4", "video/quicktime"];
const ALLOWED_MIMES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "application/pdf",
  ...VIDEO_MIMES,
];

// Shared speech-bubble SVG used on the card + modal comment pill.
// `shrink-0` keeps the icon from collapsing inside the flex pill.
const COMMENT_ICON_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" class="w-16 h-16 shrink-0"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>`;

// Right-chevron on the card.
const CHEVRON_ICON_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" class="w-20 h-20"><path d="M5 12h14M13 5l7 7-7 7"/></svg>`;

export default function warrantyRequests() {
  const root = document.querySelector("[data-warranty-root]");
  if (!root) return;

  const form = root.querySelector("[data-warranty-form]");
  const toggleBtn = root.querySelector("[data-warranty-toggle-form]");
  const cancelBtn = root.querySelector("[data-warranty-cancel]");
  const submitBtn = root.querySelector("[data-warranty-submit]");
  const feedbackEl = root.querySelector("[data-warranty-form-feedback]");
  const fileInput = root.querySelector("[data-warranty-files]");
  const listEl = root.querySelector("[data-warranty-list]");
  const emptyEl = root.querySelector("[data-warranty-empty]");
  const tabsWrap = root.querySelector("[data-warranty-tabs]");
  const dialog = document.querySelector("[data-warranty-dialog]");

  if (!form || !listEl || !tabsWrap) return;

  const emailEl = form.querySelector('input[name="contact_email"]');
  if (emailEl && !emailEl.value && window.ajax_object?.contactPersonEmail) {
    emailEl.value = window.ajax_object.contactPersonEmail;
  }

  let currentFilter = "all";
  let requests = [];

  // ---------------------- rendering ----------------------

  const esc = (s) =>
    String(s ?? "").replace(
      /[&<>"']/g,
      (c) =>
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        })[c],
    );

  const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString("et-EE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const statusPill = (status) => {
    const label = STATUS_LABELS[status] || status;
    const color = STATUS_COLORS[status] || STATUS_FALLBACK;
    return `<span class="inline-flex items-center px-12 pb-4 pt-5 rounded-full font-medium whitespace-nowrap text-14 ${color}">${esc(label)}</span>`;
  };

  // "1 kommentaar" / "N kommentaari" — Estonian partitive plural.
  const commentsLabel = (count) => {
    const n = Number(count || 0);
    return `${n} ${n === 1 ? "kommentaar" : "kommentaari"}`;
  };

  const commentsPill = (count) =>
    `<span class="inline-flex items-center gap-6 px-12 pb-4 pt-5 rounded-full bg-grey-100 text-grey-700 text-14 whitespace-nowrap">${COMMENT_ICON_SVG}<span>${commentsLabel(count)}</span></span>`;

  const numberPill = (num) =>
    `<span class="inline-flex items-center px-12 pb-4 pt-5 border rounded-full border-grey-200 text-grey-700 text-14 whitespace-nowrap">${esc(num)}</span>`;

  const updateCounts = () => {
    const counts = { all: requests.length, avatud: 0, toos: 0, lahendatud: 0 };
    requests.forEach((r) => {
      if (counts[r.status] !== undefined) counts[r.status]++;
    });
    root.querySelectorAll("[data-warranty-count]").forEach((el) => {
      const key = el.dataset.warrantyCount;
      el.textContent = counts[key] ?? 0;
    });
  };

  const renderList = () => {
    const filtered =
      currentFilter === "all"
        ? requests
        : requests.filter((r) => r.status === currentFilter);

    // Clear out any previously rendered cards but keep the empty state node.
    listEl
      .querySelectorAll("[data-warranty-card]")
      .forEach((node) => node.remove());

    if (!filtered.length) {
      if (emptyEl) {
        emptyEl.classList.remove("hidden");
        emptyEl.textContent =
          currentFilter === "all"
            ? "Pöördumisi ei ole veel esitatud."
            : "Selles vaates pöördumisi ei ole.";
      }
      return;
    }

    if (emptyEl) emptyEl.classList.add("hidden");

    const frag = document.createDocumentFragment();
    filtered.forEach((req) => {
      const card = document.createElement("button");
      card.type = "button";
      card.setAttribute("data-warranty-card", req.id);
      card.className =
        "flex items-center justify-between w-full gap-16 p-20 text-left transition border rounded-16 border-grey-200 hover:bg-grey-100 focus:outline-none focus:ring-2 focus:ring-offset-2";
      card.innerHTML = `
        <div class="flex-1 min-w-0">
          <div class="mb-8 font-medium text-16">${esc(req.title)}</div>
          <div class="flex flex-wrap items-center gap-8 text-14 text-grey-600">
            ${statusPill(req.status)}
            ${commentsPill(req.status_updates_count)}
            ${numberPill(req.request_number)}
            <span class="whitespace-nowrap">${esc(formatDate(req.created_at))}</span>
          </div>
        </div>
        <span class="flex items-center justify-center shrink-0 w-32 h-32 text-grey-700">${CHEVRON_ICON_SVG}</span>
      `;
      card.addEventListener("click", () => openDialog(req));
      frag.appendChild(card);
    });
    listEl.appendChild(frag);
  };

  // ---------------------- detail dialog ----------------------

  const renderAttachments = (req) => {
    const wrap = dialog.querySelector(
      "[data-warranty-dialog-attachments-wrap]",
    );
    const slot = dialog.querySelector("[data-warranty-dialog-attachments]");
    if (!wrap || !slot) return;

    const items = Array.isArray(req.attachments) ? req.attachments : [];
    if (!items.length) {
      wrap.classList.add("hidden");
      slot.innerHTML = "";
      return;
    }

    wrap.classList.remove("hidden");

    const allImages = items.every((a) =>
      String(a.mime_type || "").startsWith("image/"),
    );

    if (allImages) {
      slot.className = "grid grid-cols-2 md:grid-cols-3 gap-12";
      slot.innerHTML = items
        .map(
          (a) => `
        <a href="${esc(a.url)}" target="_blank" rel="noopener"
          class="block overflow-hidden rounded-12 border border-grey-200 aspect-[4/3] bg-grey-100">
          <img src="${esc(a.url)}" alt="${esc(a.original_name || "")}"
            class="w-full h-full object-cover" loading="lazy" />
        </a>`,
        )
        .join("");
    } else {
      slot.className = "grid gap-4 text-14";
      slot.innerHTML = `<ul class="grid gap-4">${items
        .map(
          (a) =>
            `<li><a href="${esc(a.url)}" target="_blank" rel="noopener" class="underline text-grey-700 hover:text-dark">${esc(
              a.original_name || a.url,
            )}</a></li>`,
        )
        .join("")}</ul>`;
    }
  };

  const openDialog = (req) => {
    if (!dialog) return;

    const setText = (sel, value) => {
      const el = dialog.querySelector(sel);
      if (el) el.textContent = value || "";
    };

    setText("[data-warranty-dialog-title]", req.title);

    const statusEl = dialog.querySelector("[data-warranty-dialog-status]");
    if (statusEl) {
      statusEl.outerHTML = `<span data-warranty-dialog-status>${statusPill(
        req.status,
      )}</span>`;
    }

    // Comment count + request number + submitted date strip.
    setText(
      "[data-warranty-dialog-comments]",
      commentsLabel(req.status_updates_count),
    );
    setText("[data-warranty-dialog-number]", req.request_number || "");
    setText("[data-warranty-dialog-submitted]", formatDate(req.created_at));

    // Kontakt — separate pills for email + phone, hide wrap when empty.
    const emailWrap = dialog.querySelector(
      "[data-warranty-dialog-email-wrap]",
    );
    const phoneWrap = dialog.querySelector(
      "[data-warranty-dialog-phone-wrap]",
    );
    const emailVal = (req.contact_email || "").trim();
    const phoneVal = (req.contact_phone || "").trim();
    setText("[data-warranty-dialog-email]", emailVal);
    setText("[data-warranty-dialog-phone]", phoneVal);
    if (emailWrap) {
      emailWrap.classList.toggle("hidden", !emailVal);
      emailWrap.classList.toggle("inline-flex", Boolean(emailVal));
    }
    if (phoneWrap) {
      phoneWrap.classList.toggle("hidden", !phoneVal);
      phoneWrap.classList.toggle("inline-flex", Boolean(phoneVal));
    }

    setText("[data-warranty-dialog-description]", req.description || "");

    renderAttachments(req);

    // Timeline (oldest -> newest visually)
    const timelineEl = dialog.querySelector("[data-warranty-dialog-timeline]");
    const timelineEmptyEl = dialog.querySelector(
      "[data-warranty-dialog-timeline-empty]",
    );
    if (timelineEl) {
      timelineEl.innerHTML = "";
      const updates = Array.isArray(req.status_updates)
        ? [...req.status_updates].sort(
            (a, b) => new Date(a.created_at) - new Date(b.created_at),
          )
        : [];
      // Show/hide empty-state placeholder + the ordered list itself.
      const hasUpdates = updates.length > 0;
      timelineEl.classList.toggle("hidden", !hasUpdates);
      if (timelineEmptyEl) {
        timelineEmptyEl.classList.toggle("hidden", hasUpdates);
      }
      const statusIconColor = {
        avatud: "text-status-avatud-ring",
        toos: "text-status-toos-ring",
        lahendatud: "text-status-lahendatud-ring",
      };
      const statusIconSvg = {
        // paper plane
        avatud:
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-12 h-12"><path d="m22 2-7 20-4-9-9-4 20-7Z"/><path d="M22 2 11 13"/></svg>',
        // clock
        toos: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-12 h-12"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>',
        // check
        lahendatud:
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="w-12 h-12"><polyline points="20 6 9 17 4 12"/></svg>',
      };
      updates.forEach((u) => {
        const iconColor = statusIconColor[u.status] || "text-grey-600";
        const iconSvg = statusIconSvg[u.status] || "";
        const nameLabel = u.created_by
          ? u.created_by_role
            ? `${u.created_by}, ${u.created_by_role}`
            : u.created_by
          : "";
        const namePill = nameLabel
          ? `<span class="inline-flex items-center px-12 pb-4 pt-5 border rounded-full border-grey-200 text-grey-700 text-14 whitespace-nowrap">${esc(
              nameLabel,
            )}</span>`
          : "";
        const li = document.createElement("li");
        li.className = "relative";
        li.innerHTML = `
          <span class="absolute -left-[36px] top-0 flex items-center justify-center w-24 h-24 rounded-full bg-white border border-grey-200 ${iconColor}">${iconSvg}</span>
          <div class="flex items-center flex-wrap gap-x-8 gap-y-4">
            ${statusPill(u.status)}
            ${namePill}
            <span class="text-14 text-grey-600 whitespace-nowrap">${esc(formatDate(u.created_at))}</span>
          </div>
          ${u.message ? `<div class="mt-8 text-16 text-grey-700 whitespace-pre-wrap">${esc(u.message)}</div>` : ""}
        `;
        timelineEl.appendChild(li);
      });
    }

    const updatedEl = dialog.querySelector("[data-warranty-dialog-updated]");
    if (updatedEl) {
      const latest = req.updated_at || req.created_at;
      updatedEl.textContent = latest
        ? `Viimati uuendatud ${formatDate(latest)}`
        : "";
    }

    if (typeof dialog.showModal === "function") {
      dialog.showModal();
    } else {
      dialog.setAttribute("open", "");
    }
  };

  const closeDialog = () => {
    if (!dialog) return;
    if (typeof dialog.close === "function") {
      dialog.close();
    } else {
      dialog.removeAttribute("open");
    }
  };

  // Close button is re-fetched via delegation so it survives any re-renders.
  dialog?.addEventListener("click", (e) => {
    if (e.target === dialog) {
      // Backdrop click.
      closeDialog();
      return;
    }
    if (e.target.closest("[data-warranty-dialog-close]")) {
      closeDialog();
    }
  });

  // ---------------------- tabs ----------------------

  tabsWrap.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-warranty-tab]");
    if (!btn) return;
    currentFilter = btn.dataset.warrantyTab;
    tabsWrap
      .querySelectorAll("[data-warranty-tab]")
      .forEach((el) => el.classList.toggle("active", el === btn));
    renderList();
  });

  // ---------------------- form toggle ----------------------

  const setFormVisible = (visible) => {
    form.classList.toggle("hidden", !visible);
    if (visible) {
      form.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  toggleBtn?.addEventListener("click", () => {
    setFormVisible(form.classList.contains("hidden"));
  });

  cancelBtn?.addEventListener("click", () => {
    form.reset();
    clearFeedback();
    setFormVisible(false);
  });

  // ---------------------- feedback helpers ----------------------

  const clearFeedback = () => {
    if (feedbackEl) feedbackEl.innerHTML = "";
  };

  const setFeedback = (message, kind = "info") => {
    if (!feedbackEl) return;
    const palette =
      kind === "error"
        ? "bg-red-100 text-red-800"
        : kind === "success"
          ? "bg-green-100 text-green-800"
          : "bg-grey-100 text-grey-800";
    feedbackEl.innerHTML = `<div class="px-12 py-8 rounded-8 ${palette} text-14">${esc(
      message,
    )}</div>`;
  };

  // ---------------------- file validation ----------------------

  const validateFiles = () => {
    if (!fileInput || !fileInput.files.length) return true;
    if (fileInput.files.length > MAX_FILES) {
      setFeedback(`Maksimaalselt ${MAX_FILES} faili.`, "error");
      return false;
    }
    let totalSize = 0;
    for (const file of fileInput.files) {
      if (!ALLOWED_MIMES.includes(file.type)) {
        setFeedback(`"${file.name}" — lubamatu failitüüp.`, "error");
        return false;
      }
      const isVideo = VIDEO_MIMES.includes(file.type);
      const maxFileSize = isVideo
        ? MAX_VIDEO_FILE_SIZE
        : MAX_STANDARD_FILE_SIZE;
      if (file.size > maxFileSize) {
        setFeedback(
          `"${file.name}" on liiga suur (max ${isVideo ? 50 : 10} MB).`,
          "error",
        );
        return false;
      }
      totalSize += file.size;
      if (totalSize > MAX_TOTAL_SIZE) {
        setFeedback("Manuste kogumaht võib olla kuni 100 MB.", "error");
        return false;
      }
    }
    return true;
  };

  // ---------------------- AJAX ----------------------

  const ajaxUrl = window.ajax_object?.ajax_url;
  const postId = window.ajax_object?.post_id;

  const loadRequests = async () => {
    if (!ajaxUrl || !postId) return;
    const body = new URLSearchParams({
      action: "get_warranty_requests",
      post_id: String(postId),
    });
    try {
      const res = await fetch(ajaxUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
        credentials: "same-origin",
      });
      const json = await res.json();
      if (json?.success && Array.isArray(json.data?.requests)) {
        requests = json.data.requests;
      } else {
        requests = [];
      }
    } catch (err) {
      requests = [];
      console.error("[garantii] load failed", err);
    }
    updateCounts();
    renderList();
  };

  const submitForm = async (e) => {
    e.preventDefault();
    clearFeedback();

    // Phone is required (matches LiviData validator); check before hitting AJAX
    // so the user sees an Estonian message instead of a generic 400/422.
    const phoneEl = form.querySelector('input[name="contact_phone"]');
    if (phoneEl && phoneEl.value.trim() === "") {
      setFeedback("Palun sisesta telefon.", "error");
      phoneEl.focus();
      return;
    }

    if (!validateFiles()) return;
    if (!ajaxUrl || !postId) return;

    const fd = new FormData(form);
    fd.set("action", "create_warranty_request");
    fd.set("post_id", String(postId));

    submitBtn.disabled = true;
    const originalLabel = submitBtn.textContent;
    submitBtn.textContent = "Saadan...";

    try {
      const res = await fetch(ajaxUrl, {
        method: "POST",
        body: fd,
        credentials: "same-origin",
      });
      const json = await res.json();
      if (json?.success) {
        setFeedback(json.data?.message || "Päring on saadetud.", "success");
        form.reset();
        if (emailEl && window.ajax_object?.contactPersonEmail) {
          emailEl.value = window.ajax_object.contactPersonEmail;
        }
        setTimeout(() => {
          clearFeedback();
          setFormVisible(false);
        }, 1800);
        await loadRequests();
      } else {
        const msg =
          typeof json?.data === "string"
            ? json.data
            : "Päringu saatmine ebaõnnestus.";
        setFeedback(msg, "error");
      }
    } catch (err) {
      console.error("[garantii] submit failed", err);
      setFeedback("Päringu saatmine ebaõnnestus.", "error");
    } finally {
      submitBtn.disabled = false;
      submitBtn.textContent = originalLabel;
    }
  };

  form.addEventListener("submit", submitForm);

  // Initial load
  loadRequests();
}
